/**
 * Intake draft persistence (localStorage) so an interrupted secretary can resume.
 * Demo-only convenience; safe no-ops if storage is unavailable.
 *
 * Fase 3: the wizard shape changed (sector-aware, entity-type branched, KYC + roles).
 * The storage key is VERSIONED (`:v3` — el paso 'documentos' se antepuso al asistente
 * y el índice posicional cambió de semántica) so stale drafts (a different shape) are
 * never read back — the old key is simply orphaned, and any parse mismatch falls
 * through the try/catch to `null`. Files (staged documents) are NOT drafted — a
 * `File` can't serialize — so a resumed draft re-asks for the attachments.
 */

const KEY = 'cumpli:intake-draft:v3';

/** One related person (Sociedad/Fundación) — scalar fields only (no staged files). */
export interface DraftPerson {
  localId: string;
  rol: string;
  posicion: string;
  name: string;
  tipoDocumento: string;
  numeroDocumento: string;
  nacionalidad: string;
  paisResidenciaFiscal: string;
  ownershipPct: string;
  porcentajePatrimonial: string;
  pepPropio: boolean;
  pepFamiliar: boolean;
  usPerson: boolean;
}

export interface IntakeDraft {
  entityType: string;
  idx: number;
  // Clave del paso actual — el array de pasos puede cambiar de forma (p.ej. 'escanear'
  // se elimina tras leer la cédula en el paso inicial), así que restaurar por CLAVE es
  // estable donde el índice posicional no lo es. Opcional para deserializar drafts previos.
  stepKey?: string;
  // Identidad (persona natural) / entidad (persona jurídica).
  name: string;
  docId: string;
  // ISO 3166-1 alpha-3 citizenship country (natural persons); drives ID-number
  // formatting. Optional so older drafts (pre-citizenship) deserialize cleanly.
  country?: string;
  birthDate: string;
  perfil: string;
  email: string;
  phone: string;
  // Domicilio (PN y PJ) — capturado por OCR de recibo de servicio o a mano; para PJ
  // este mismo valor también alimenta `direccionFisica` al construir el payload.
  // Optional for back-compat with drafts saved before this field existed.
  direccion?: string;
  // Persona jurídica — datos de la entidad. No longer a separate input (the shared
  // "domicilio" step is now the only address capture point); kept optional so a
  // stale draft that still has this key from before the consolidation deserializes
  // without crashing — it is simply never read back into state.
  direccionFisica?: string;
  folio: string;
  constitutionDate: string;
  paisesActividad: string[];
  // KYC núcleo + insumos de matriz (claves de catálogo).
  proposito: string;
  profesion: string;
  fuenteFondos: string;
  nacionalidad: string;
  paisResidenciaFiscal: string;
  paisDomicilio: string;
  canalVia: string;
  producto: string;
  pepPropio: boolean;
  pepFamiliar: boolean;
  usPerson: boolean;
  multiplesNacionalidades: boolean;
  // Personas relacionadas (Sociedad / Fundación).
  people: DraftPerson[];
}

/** A draft is only meaningful once the user has entered identifying info. */
export function isMeaningful(d: IntakeDraft | null): d is IntakeDraft {
  return (
    !!d &&
    (d.name.trim() !== '' || d.docId.trim() !== '' || (d.people?.length ?? 0) > 0)
  );
}

export function loadDraft(): IntakeDraft | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as IntakeDraft) : null;
  } catch {
    return null;
  }
}

export function saveDraft(d: IntakeDraft): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(d));
  } catch {
    /* ignore */
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
